import React, { useState, useEffect, useRef } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';

/**
 * BlockInsertZone Component
 * 
 * Renders an invisible hover zone between blocks that shows a plus icon
 * after 0.7 seconds of hover, allowing users to insert a new block
 */
const BlockInsertZone = ({
  afterBlockId,
  onInsert,
  readOnly = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showPlus, setShowPlus] = useState(false);
  const timerRef = useRef(null);

  // Handle mouse enter - start timer
  const handleMouseEnter = () => {
    if (readOnly) return;
    
    setIsHovered(true);
    
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    // Set timer for 0.7 seconds
    timerRef.current = setTimeout(() => {
      setShowPlus(true);
    }, 700);
  };

  // Handle mouse leave - reset state
  const handleMouseLeave = () => {
    setIsHovered(false);
    setShowPlus(false);
    
    // Clear timer if it exists
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Handle click on plus icon
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (readOnly || !onInsert) return;
    
    onInsert(afterBlockId);
    
    // Reset state after click
    setIsHovered(false);
    setShowPlus(false);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Don't render in read-only mode
  if (readOnly) {
    return null;
  }

  return (
    <div
      className={`block-insert-zone ${isHovered ? 'hovered' : ''} ${showPlus ? 'expanded' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className="block-insert-line" aria-hidden="true" />
      {showPlus && (
        <button
          type="button"
          onClick={handleClick}
          className="block-insert-button"
          title="Insert new block"
          aria-label="Insert new block"
        >
          <PlusIcon />
        </button>
      )}
    </div>
  );
};

export default BlockInsertZone;

